'use client';

import { createClient } from '@/lib/supabase/client';

export interface Archive {
  id: string;
  title: string;
  description: string;
  coverImageUrl: string | null;
  eraId: string | null;
  topicId: string | null;
  region: string | null;
  creatorId: string | null;
  creatorName: string | null;
  isPublished: boolean;
  publishedAt: string | null;
  contributionCount: number;
  createdAt: string;
  updatedAt: string;
}

export interface ArchiveContribution {
  id: string;
  archiveId: string;
  contributionId: string;
  contributorId: string | null;
  contributorName: string | null;
  status: 'pending' | 'accepted' | 'rejected';
  note: string | null;
  reviewedAt: string | null;
  createdAt: string;
  contributionTitle?: string;
  contributionExcerpt?: string;
  contributionCertaintyLabel?: string;
}

async function getCreatorNames(supabase: any, creatorIds: string[]): Promise<Record<string, string>> {
  if (!creatorIds.length) return {};
  const uniqueIds = [...new Set(creatorIds.filter(Boolean))];
  if (!uniqueIds.length) return {};
  const { data } = await supabase
    .from('user_profiles')
    .select('id, full_name, username')
    .in('id', uniqueIds);
  const map: Record<string, string> = {};
  for (const row of data || []) {
    map[row.id] = row.full_name || row.username || 'Unknown';
  }
  return map;
}

function mapArchiveRow(row: any, creatorNames: Record<string, string>): Archive {
  return {
    id: row.id,
    title: row.title,
    description: row.description,
    coverImageUrl: row.cover_image_url,
    eraId: row.era_id,
    topicId: row.topic_id,
    region: row.region,
    creatorId: row.creator_id,
    creatorName: row.creator_id ? (creatorNames[row.creator_id] || 'Unknown') : 'Unknown',
    isPublished: row.is_published,
    publishedAt: row.published_at,
    contributionCount: row.contribution_count,
    createdAt: row.created_at,
    updatedAt: row.updated_at,
  };
}

export async function getPublishedArchives(limit = 6): Promise<Archive[]> {
  const supabase = createClient();
  try {
    const { data, error } = await supabase
      .from('archives')
      .select('id, title, description, cover_image_url, era_id, topic_id, region, creator_id, is_published, published_at, contribution_count, created_at, updated_at')
      .eq('is_published', true)
      .order('published_at', { ascending: false })
      .limit(limit);

    if (error) {
      console.error('getPublishedArchives error:', error.message);
      return [];
    }

    const rows = data || [];
    const creatorIds = rows.map((r: any) => r.creator_id).filter(Boolean);
    const creatorNames = await getCreatorNames(supabase, creatorIds);
    return rows.map((row: any) => mapArchiveRow(row, creatorNames));
  } catch (error: any) {
    console.error('getPublishedArchives error:', error.message);
    return [];
  }
}

export async function getAllPublishedArchives(): Promise<Archive[]> {
  const supabase = createClient();
  try {
    const { data, error } = await supabase
      .from('archives')
      .select('id, title, description, cover_image_url, era_id, topic_id, region, creator_id, is_published, published_at, contribution_count, created_at, updated_at')
      .eq('is_published', true)
      .order('published_at', { ascending: false });

    if (error) {
      console.error('getAllPublishedArchives error:', error.message);
      return [];
    }

    const rows = data || [];
    const creatorIds = rows.map((r: any) => r.creator_id).filter(Boolean);
    const creatorNames = await getCreatorNames(supabase, creatorIds);
    return rows.map((row: any) => mapArchiveRow(row, creatorNames));
  } catch (error: any) {
    console.error('getAllPublishedArchives error:', error.message);
    return [];
  }
}

export async function getArchiveById(id: string): Promise<Archive | null> {
  const supabase = createClient();
  try {
    const { data, error } = await supabase
      .from('archives')
      .select('id, title, description, cover_image_url, era_id, topic_id, region, creator_id, is_published, published_at, contribution_count, created_at, updated_at')
      .eq('id', id)
      .maybeSingle();

    if (error) {
      console.error('getArchiveById error:', error.message);
      return null;
    }
    if (!data) return null;

    const creatorNames = await getCreatorNames(supabase, data.creator_id ? [data.creator_id] : []);
    return mapArchiveRow(data, creatorNames);
  } catch (error: any) {
    console.error('getArchiveById error:', error.message);
    return null;
  }
}

export async function getArchiveContributions(archiveId: string): Promise<ArchiveContribution[]> {
  const supabase = createClient();
  try {
    const { data, error } = await supabase
      .from('archive_contributions')
      .select('id, archive_id, contribution_id, contributor_id, status, note, reviewed_at, created_at')
      .eq('archive_id', archiveId)
      .order('created_at', { ascending: false });

    if (error) {
      console.error('getArchiveContributions error:', error.message);
      return [];
    }

    const rows = data || [];

    // Fetch contributor names
    const contributorIds = rows.map((r: any) => r.contributor_id).filter(Boolean);
    const contributorNames = await getCreatorNames(supabase, contributorIds);

    // Fetch contribution details
    const contributionIds = rows.map((r: any) => r.contribution_id).filter(Boolean);
    let contributionMap: Record<string, { title: string; excerpt: string | null; certainty_label: string | null }> = {};
    if (contributionIds.length) {
      const { data: contribs } = await supabase
        .from('contributions')
        .select('id, title, excerpt, certainty_label')
        .in('id', contributionIds);
      for (const c of contribs || []) {
        contributionMap[c.id] = { title: c.title, excerpt: c.excerpt, certainty_label: c.certainty_label };
      }
    }

    return rows.map((row: any) => ({
      id: row.id,
      archiveId: row.archive_id,
      contributionId: row.contribution_id,
      contributorId: row.contributor_id,
      contributorName: row.contributor_id ? (contributorNames[row.contributor_id] || 'Anonymous') : 'Anonymous',
      status: row.status,
      note: row.note,
      reviewedAt: row.reviewed_at,
      createdAt: row.created_at,
      contributionTitle: contributionMap[row.contribution_id]?.title || '',
      contributionExcerpt: contributionMap[row.contribution_id]?.excerpt || '',
      contributionCertaintyLabel: contributionMap[row.contribution_id]?.certainty_label || '',
    }));
  } catch (error: any) {
    console.error('getArchiveContributions error:', error.message);
    return [];
  }
}

export async function submitContributionToArchive(
  archiveId: string,
  contributionId: string,
  note?: string
): Promise<boolean> {
  const supabase = createClient();
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) return false;

  try {
    const { error } = await supabase
      .from('archive_contributions')
      .insert({
        archive_id: archiveId,
        contribution_id: contributionId,
        contributor_id: user.id,
        status: 'pending',
        note: note || null,
      });

    if (error) {
      console.error('submitContributionToArchive error:', error.message);
      return false;
    }
    return true;
  } catch (error: any) {
    console.error('submitContributionToArchive error:', error.message);
    return false;
  }
}

export async function reviewArchiveContribution(
  archiveContributionId: string,
  status: 'accepted' | 'rejected'
): Promise<boolean> {
  const supabase = createClient();
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) return false;

  try {
    const { error } = await supabase
      .from('archive_contributions')
      .update({
        status,
        reviewed_at: new Date().toISOString(),
      })
      .eq('id', archiveContributionId);

    if (error) {
      console.error('reviewArchiveContribution error:', error.message);
      return false;
    }
    return true;
  } catch (error: any) {
    console.error('reviewArchiveContribution error:', error.message);
    return false;
  }
}

export async function getUserPublishedContributions(): Promise<{ id: string; title: string; excerpt: string | null }[]> {
  const supabase = createClient();
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) return [];

  try {
    const { data, error } = await supabase
      .from('contributions')
      .select('id, title, excerpt')
      .eq('author_id', user.id)
      .eq('status', 'published')
      .order('created_at', { ascending: false });

    if (error) {
      return [];
    }
    return (data || []).map((row: any) => ({
      id: row.id,
      title: row.title,
      excerpt: row.excerpt,
    }));
  } catch (error: any) {
    console.error('getUserPublishedContributions error:', error.message);
    return [];
  }
}

export async function getArchivesForContribution(contributionId: string): Promise<{ archiveId: string; archiveTitle: string; status: 'pending' | 'accepted' | 'rejected' }[]> {
  const supabase = createClient();
  try {
    const { data, error } = await supabase
      .from('archive_contributions')
      .select('archive_id, status')
      .eq('contribution_id', contributionId);

    if (error) {
      console.error('getArchivesForContribution error:', error.message);
      return [];
    }

    const rows = data || [];
    if (!rows.length) return [];

    const archiveIds = rows.map((r: any) => r.archive_id);
    const { data: archives } = await supabase
      .from('archives')
      .select('id, title')
      .in('id', archiveIds);

    const archiveMap: Record<string, string> = {};
    for (const a of archives || []) {
      archiveMap[a.id] = a.title;
    }

    return rows.map((r: any) => ({
      archiveId: r.archive_id,
      archiveTitle: archiveMap[r.archive_id] || 'Unknown Archive',
      status: r.status,
    }));
  } catch (error: any) {
    console.error('getArchivesForContribution error:', error.message);
    return [];
  }
}
